import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Search, User } from "lucide-react";
import { BsCart3 } from "react-icons/bs";
import { TiShoppingCart } from "react-icons/ti";
import useAuth from "../hooks/useAuth";
import { useGetUserByIdQuery, useGetUserByIdVQuery } from "../features/users/usersApiSlice";
import { useSelector } from "react-redux";
import { selectCartItems } from "../features/users/userSlice";
import logo from "../assets/images/logo.png";
import { userImageUrlPrefix, ROUTES } from "../assets/data";
import cart from "../assets/images/cart.svg"
import { MdSearch } from "react-icons/md";
import { FiSearch } from "react-icons/fi";
import { useGetSearchProductsQuery } from "../features/products/productsApiSlice";
import Loading from "./Loading";
import { ProductDetailed } from "./Product";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isStoreMenuOpen, setIsStoreMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const { id } = useAuth();
  const { data: user, isSuccess } = useGetUserByIdVQuery(id);
  const location = useLocation();
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const cartItems = useSelector(selectCartItems);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [showDropdown, setShowDropdown] = useState(false);

  const {
    data: searchResults,
    error,
    isLoading,
  } = useGetSearchProductsQuery({ query, page });

  const handleSearch = (e) => {
    e.preventDefault();
    // setSearchTerm(query);
    setPage(1); // Reset to first page on new search
    setShowDropdown(false); // Close the dropdown
  };

  useEffect(() => {
    if (query.length > 0) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  }, [query]);



  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);

    const handleClickOutside = (event) => {
      if (
        isMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isMenuOpen]);

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsStoreMenuOpen(false);
  };

  const isStoreActive = [ROUTES.MY_STORE, ROUTES.SELL].includes(location.pathname);
  const isLandingPage = location.pathname === ROUTES.HOME;

  const NavItem = ({ to, children, onClick, className }) => (
    <NavLink
      to={to}
      onClick={() => {
        onClick?.();
        closeMenu();
        window.scrollTo({ top: 0 });
      }}
      className={({ isActive }) =>
        `px-4 py-2 rounded-lg transition-all duration-200 ${
          isActive
            ? "bg-blue-50 text-blue-700 font-medium"
            : "hover:bg-blue-50 text-gray-700 hover:text-blue-700"
        } ${className || ""}`
      }
    >
      {children}
    </NavLink>
  );

  const StoreDropdown = () => (
    <div
      className="hidden md:block absolute top-full left-0 w-48 -mt-2 bg-white rounded-lg shadow-lg overflow-hidden border border-gray-100"
      onMouseEnter={() => !isMobile && setIsStoreMenuOpen(true)}
      onMouseLeave={() => !isMobile && setIsStoreMenuOpen(false)}
    >
      <div className="flex flex-col py-2 gap-1">
        <NavItem to={ROUTES.MY_STORE}>My Listings</NavItem>
        <NavItem to={ROUTES.SELL}>Create Post</NavItem>
      </div>
    </div>
  );

  const ProfileButton = ({ className = "", showName = true }) => (
    <NavLink
      to={isSuccess ? ROUTES.EDIT_PROFILE : ROUTES.LOGIN}
      onClick={closeMenu}
      className={({ isActive }) =>
        `hidden md:flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
          isActive
            ? "bg-blue-50 text-blue-700"
            : "hover:bg-blue-50 text-gray-700 hover:text-blue-700"
        } ${className}`
      }
    >
      {isSuccess && user?.userImage ? (
        <div className="relative">
          <img
            src={user.userImage}
            alt="Profile"
            className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
          />
        </div>
      ) : (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
          <User className="w-5 h-5 text-white" />
        </div>
      )}
      {showName && <span className="font-medium">{isSuccess ? user?.firstName : "Join"}</span>}
    </NavLink>
  );

  const CartButton = () => (
    <NavLink
      to={ROUTES.CART}
      className={({ isActive }) =>
        `relative px-4 py-2 rounded-lg transition-all duration-200 ${
          isActive
            ? "bg-blue-50 text-blue-700"
            : "hover:bg-blue-50 text-gray-700 hover:text-blue-700"
        }`
      }
    >
      <div className="flex items-end gap-2">
        <div className="relative">
          <img src={cart} alt="cart" className="w-8 h-8" />
          <div className="absolute top-[1px] right-[5px] min-w-[16px] h-[16px] text-blue-600 text-[1.1rem] font-medium rounded-full flex items-center justify-center leading-none">
            {cartItems.length}
          </div>
        </div>
        <span>Cart</span>
      </div>
    </NavLink>
  );

  return (
    <nav className={`bg-white dark:bg-neutral-800 dark:border-b dark:border-neutral-700 bg-opacity-95 backdrop-blur-sm fixed top-0 left-0 right-0 z-50 ${!isLandingPage && "shadow-md"}`}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-12">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link
            to={ROUTES.HOME}
            className="flex items-center space-x-2"
            onClick={closeMenu}
          >
            <img src={logo} className="h-8" alt="Logo" />
          </Link>

          {/* Middle navigation group - Desktop */}
          <div 
            className="flex-grow border border-gray-400 h-[40px] ml-8 mr-3 px-2 rounded-md flex justify-start items-center"
            onFocusOut={(e) => {
              // Only hide if focus is leaving the entire search container
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setIsSearchFocused(false);
              }
            }}
          >
            <FiSearch className="text-[1.3rem] text-gray-500"/>
            <input 
              type="text" 
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch(e);
                }
              }}
              placeholder="Search products..."
              className="w-full px-2 outline-none h-full" 
              onFocus={() => setIsSearchFocused(true)}
            />
            {isSearchFocused && (
              <div 
                className={`absolute top-[100px] left-0 right-0 bg-black/60 h-[calc(100vh-100px)] overflow-scroll flex flex-col justify-start items-center px-[5%]`} 
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                onClick={(e) => {
                  // Hide dropdown when clicking on search results (but not on the input)
                  if (e.target !== e.currentTarget.querySelector('input')) {
                    setIsSearchFocused(false);
                  }
                }}
              >
                <div className="pt-[40px] w-full p-0 flex flex-col justify-start items-center grow">
                  {isLoading && <Loading />}
                  {error && <p className="text-slate-400 italic bg-red-20 w-full -mt-2">{error?.data?.message}</p>}
                  {/* {searchResults && searchResults.results.length === 0 && <p>No products found!</p>} */}
                  {searchResults && !error && searchResults.results.length > 0 && searchResults.results.map((product) => (
                    console.log(searchResults),
                    <ProductDetailed key={product._id} product={product} page="search" />
                  ))}
                </div>

                <div className="flex justify-between mt-4">
                  {page > 1 && <button onClick={() => setPage(page - 1)} className="bg-gray-300 p-2 rounded-md">Previous</button>}
                  {searchResults && searchResults.results.length === 20 && (
                    <button onClick={() => setPage(page + 1)} className="bg-gray-300 p-2 rounded-md">Next</button>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* Right side navigation group - Desktop */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="flex items-center">
              {/* Store Dropdown */}
              <div className="relative">
                <button
                  className={`px-4 py-2 rounded-lg transition-all duration-200 flex items-center space-x-1 ${
                    isStoreActive || isStoreMenuOpen
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                  }`}
                  onMouseEnter={() => setIsStoreMenuOpen(true)}
                  onMouseLeave={() => setIsStoreMenuOpen(false)}
                >
                  <span>Store</span>
                  <ChevronDown
                    className={`w-4 h-4 transform transition-transform ${
                      isStoreMenuOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isStoreMenuOpen && <StoreDropdown />}
              </div>

              {/* <NavItem to={ROUTES.SEARCH}>
                <div className="flex items-center space-x-1">
                  <Search className="w-4 h-4" />
                  <span>Search</span>
                </div>
              </NavItem> */}


              <ProfileButton showName={true} />
              <CartButton />
            </div>
          </div>

          {/* Mobile Profile and Menu Buttons */}
          <div className="md:hidden flex items-center space-x-2">
            <ProfileButton showName={false} />
            <button
              ref={menuButtonRef}
              className="p-2 rounded-lg hover:bg-blue-50 text-gray-700 hover:text-blue-700 transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div ref={menuRef} className="md:hidden py-4 space-y-2 flex flex-col border-t border-gray-100">
            <NavItem to={ROUTES.MY_STORE} className="w-full">
              My Listings
            </NavItem>
            <NavItem to={ROUTES.SELL} className="w-full">
              Create Post
            </NavItem>
            {/* <NavItem to={ROUTES.SEARCH} className="w-full">
              Search
            </NavItem> */}
            <NavItem to={ROUTES.CART} className="w-full">
              <div className="flex items-center justify-between">
                <span>Cart</span>
                <div className="flex items-center space-x-2">
                  <TiShoppingCart className="w-5 h-5" />
                  <span className="bg-blue-600 text-white text-xs font-medium px-2 py-1 rounded-full">
                    {cartItems.length} items
                  </span>
                </div>
              </div>
            </NavItem>
            <NavItem to={isSuccess ? ROUTES.EDIT_PROFILE : ROUTES.LOGIN} className="w-full">
              <div className="flex items-center space-x-2">
                {isSuccess && user?.userImage ? (
                  <img
                    src={user.userImage}
                    alt="Profile"
                    className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                )}
                <span className="font-medium">{isSuccess ? user?.firstName : "Join"}</span>
              </div>
            </NavItem>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
